import { __decorate, __metadata, __param } from "tslib";
import _ from 'lodash';
import VueRouter from 'vue-router';
import { inject, injectable } from 'inversify';
import { Symbols } from '../../symbols';
import { isAxiosError } from './exception';
import { isAsyncCancelError } from '../../utils/exception';
/**
 * 由于全局的异常处理器支持异步处理目前解决方式是先处理掉异常
 * ` event.preventDefault()
 *  event.stopImmediatePropagation()`
 * 再加入$tag, 第二次判断如果有$tag, 就不处理了, 丢给默认异常处理器或者第三方报告
 */
const $tag = Object.create(null);
function isUnhandled(e) {
    return e && e.$tag === $tag;
}
function reThrow(e) {
    e.$tag = $tag;
    setTimeout(() => {
        throw e;
    }, 0);
}
let ErrorHandlerService = class ErrorHandlerService {
    constructor(vue, systemService, i18n) {
        this.vue = vue;
        this.systemService = systemService;
        this.i18n = i18n;
        this._handlers = [];
        this.onError = (e) => {
            return this.handleErrorWrapper(e, e.error);
        };
        this.onUnHandledRejection = (e) => {
            return this.handleErrorWrapper(e, e.reason);
        };
        // #region Common Display
        this._errorDisplayProxy = (message) => {
            console.error(message);
        };
        this.errorDisplay = (message) => {
            this._errorDisplayProxy(message);
        };
    }
    start() {
        window.addEventListener('error', this.onError);
        window.addEventListener('unhandledrejection', this.onUnHandledRejection);
        const originHandler = this.vue.config.errorHandler;
        this._vueOriginHandler = originHandler;
        this.vue.config.errorHandler = (err, vm, info) => {
            this.vue.util.warn(('Error in ' + info + ': "' + (err.toString()) + '"'), vm);
            const handled = this.handleError(err);
            if (handled) {
                return;
            }
            if (originHandler) {
                return originHandler(err, vm, info);
            }
        };
    }
    end() {
        window.removeEventListener('error', this.onError);
        window.removeEventListener('unhandledrejection', this.onUnHandledRejection);
        this.vue.config.errorHandler = this._vueOriginHandler;
    }
    addHandler(handler) {
        if (_.indexOf(this._handlers, handler) === -1) {
            this._handlers.push(handler);
        }
        else {
            console.error('ErrorHandlerService: handler has existed:', handler);
        }
        return () => {
            const idx = _.indexOf(this._handlers, handler);
            if (idx !== -1) {
                this._handlers.splice(idx, 1);
            }
        };
    }
    ignoreCommonError(err) {
        const { isNavigationFailure, NavigationFailureType, } = VueRouter;
        if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
            return true;
        }
        if (isNavigationFailure(err, NavigationFailureType.redirected)) {
            return true;
        }
        if (isAsyncCancelError(err)) {
            return true;
        }
        return false;
    }
    /**
     *调试器异常
     */
    isDevtoolsError(err) {
        if (err) {
            if (err.name === 'SyntaxError' || err.name === 'EvalError') {
                return true;
            }
        }
        return false;
    }
    commonError(err) {
        if (isAxiosError(err)) {
            if (_.includes(['4', '5'], `${err.response?.status}`.substring(0, 1))) {
                this.errorDisplay(this.i18n.t('loginFail.unknown'));
                return true;
            }
            if (err.request.status === 0) {
                this.errorDisplay(this.i18n.t('loginFail.unknown'));
                return true;
            }
        }
        return false;
    }
    async handleError(err) {
        const { _handlers, } = this;
        if (this.ignoreCommonError(err)) {
            this.systemService.reportVerboseError(err);
            return true;
        }
        this.systemService.reportError(err);
        for (let i = 0, len = _handlers.length; i < len; i++) {
            const handler = _handlers[i];
            try {
                const ret = await handler(err);
                if (ret) {
                    return true;
                }
            }
            catch (e) {
                console.error(e);
            }
        }
        if (this.commonError(err)) {
            return true;
        }
        return false;
    }
    async handleErrorWrapper(event, error) {
        if (!error) {
            return;
        }
        // 调试器异常不处理
        if (this.isDevtoolsError(error)) {
            return;
        }
        if (!isUnhandled(error)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            const handled = await this.handleError(error);
            if (!handled) {
                reThrow(error);
            }
        }
    }
    setErrorDisplay(fn) {
        this._errorDisplayProxy = fn;
    }
};
ErrorHandlerService = __decorate([
    injectable(),
    __param(0, inject(Symbols.Vue)),
    __param(1, inject(Symbols.SystemService)),
    __param(2, inject(Symbols.I18n)),
    __metadata("design:paramtypes", [Object, Object, Object])
], ErrorHandlerService);
export { ErrorHandlerService };
